import { useTranslation } from "react-i18next";
import * as S from "./styles";
import { Input } from "components/Input";
import { Controller, FieldErrors, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useEffect, useState } from "react";
import { useSessionStorage } from "context/sessionStorage";
import { useAddressSchema } from "pages/Checkin/schemas/Address";
import { DropdownWithError } from "components/DropdownWithError";
import { FooterWithButtons } from "pages/Checkin/components/FooterWithButtons";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { NewInput } from "components/NewInput";
import { IMaskInput } from "react-imask";

type AddressFormDataNatinal = {
    cep: string,
    address: string,
    complement: string,
    number: string,
    district: string,
    city: string,
    state: {
        value: string,
        label: string
    }
}

type AddressFormDataInternational = {
    fullAddress: string,
    cityInternational: string,
    province: string
}

export const AddressCheckin = () => {
    const { t } = useTranslation();
    const [contryYouLive, setCountryYouLive] = useState<string>('')
    const [states, setStates] = useState<{ value: string, label: string }[]>([])
    const [documentValidation, setDocumentValidation] = useState<string>('')
    const { saveToSessionStorage, getFromSessionStorage } = useSessionStorage()
    const { schema, schemaInternational, schemaNational } = useAddressSchema(contryYouLive);
    const navigate = useNavigate();

    type NationalAddressFormData = z.infer<typeof schemaNational>;
    type InternationalAddressFormData = z.infer<typeof schemaInternational>;
    type FormData = NationalAddressFormData | InternationalAddressFormData;

    useEffect(() => {
        const data: AddressFormDataNatinal | AddressFormDataInternational = getFromSessionStorage('address')
        if (!data) return
        if ('cep' in data) {
            setValue('address', data.address ?? '')
            setValue('cep', data.cep ?? '')
            setValue('city', data.city ?? '')
            setValue('complement', data.complement ?? '')
            setValue('district', data.district ?? '')
            setValue('number', data.number ?? '')
            setValue('state', data.state ?? { value: '', label: '' })
        } else {
            setValue('fullAddress', data.fullAddress ?? '')
            setValue('cityInternational', data.cityInternational ?? '')
            setValue('province', data.province ?? '')
        }
    }, [])

    const { handleSubmit, formState: { errors }, setValue, control, trigger, watch } = useForm<FormData>({
        resolver: zodResolver(schema),
    });

    const pathnameActual: string = window.location.pathname.endsWith('/') ? window.location.pathname.slice(0, -1) : window.location.pathname.slice(0, window.location.pathname.lastIndexOf('/'));


    function validateError(field: string, errors: FieldErrors<FormData>): string | undefined {
        // Attempting a direct access and handling it as any to bypass the compile-time check
        // NOTE: This reduces type safety and should be used with caution
        const error = (errors as any)[field]?.message;
        return error;
    }

    const OnSubmit = (data: FormData) => {
        if (!!errors) {

            saveToSessionStorage('address', { ...data, validateAddress: true });
            if (documentValidation === 'PASSPORT') {
                navigate(`${pathnameActual}/verify-passport`)
            } else {
                navigate(`${pathnameActual}/verify-document`)
            }
        }
    }

    const handleBack = () => {
        navigate(`${pathnameActual}/personal-info`)
    }

    useEffect(() => {
        const data = getFromSessionStorage('address')
        const getLocationCep = (async () => {
            try {
                const { data } = await axios.get(`https://viacep.com.br/ws/${watch('cep')}/json/`)
                setValue('address', data.logradouro)
                setValue('district', data.bairro)
                setValue('city', data.localidade)
                const state = states.find((state) => state.label === data.uf)
                console.log(state, 'state')
                setValue('state', { label: data.uf, value: data.uf })
                console.log(data, 'data')
            } catch (error) {
            }
        })
        if (watch('cep') && watch('cep').length === 8 && data?.cep !== watch('cep')) {
            getLocationCep()
        }
    }, [watch('cep')])


    useEffect(() => {
        const dataPersonal = getFromSessionStorage('personal-info')
        console.log(dataPersonal, 'dataPersonal')
        if (dataPersonal) {
            setCountryYouLive(dataPersonal.countryYouLive.label)
            setDocumentValidation(dataPersonal.documentType)
        }
        const getStates = async () => {
            try {
                const data = await axios.get('https://servicodados.ibge.gov.br/api/v1/localidades/estados?orderBy=nome')
                const states = data.data.map((state: any) => {
                    return { value: state.sigla, label: state.sigla }
                })
                setStates(states)
            } catch (error) {

            }
        }

        getStates()
    }, [])

    return (<>
        {contryYouLive === 'Brasil' || contryYouLive === 'Brazil' ? (
            <S.AddressContainer>
                <h1>{t('checkin.address.title')}</h1>
                <h4>{t('checkin.address.subtitle')}</h4>
                <S.FormContainer>
                    <S.LeftColumn>
                        <Controller
                            name="cep"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <NewInput mask={''} width={'100%'} name="cep" htmlFor="cep" placeholder="" title={t('checkin.address.forms.label.cep')} type="text" errorText={validateError('cep', errors)}>
                                    <IMaskInput
                                        width={'100%'}
                                        mask={[{ mask: "00000-000" }]}
                                        radix="."
                                        unmask={true} // true|false|'typed'
                                        value={value}
                                        onAccept={(value, mask) => {
                                            onChange(value)
                                        }}
                                        placeholder=""
                                        onBlur={() => trigger('cep')}
                                    />
                                </NewInput>
                            )
                            }
                        />
                        <Controller
                            name="number"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <Input width={"100%"} id="number" onBlur={() => trigger('number')} htmlFor="number" errorText={validateError('number', errors)} onChange={onChange} placeholder={''} title={t('checkin.address.forms.label.number')} value={value} />
                            )
                            }
                        />
                        <Controller
                            name="district"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <Input width={"100%"} id="ditrict" onBlur={() => trigger('district')} htmlFor="ditrict" errorText={validateError('ditrict', errors)} onChange={onChange} placeholder={''} title={t('checkin.address.forms.label.district')} value={value} />
                            )
                            }
                        />
                        <Controller
                            name={'state'}
                            control={control}
                            render={({ field }) => (
                                <DropdownWithError widthDrop={''} label={t('checkin.address.forms.label.state')} name="state" value={field.value} onChange={(option) => option ? field.onChange(option) : field.onChange(undefined)}
                                    placeholder={t('checkin.address.forms.placeholder.state')} options={states} errorText={validateError('state', errors)} />
                            )}
                        />
                    </S.LeftColumn>
                    <S.RigthColumn>
                        <Controller
                            name="address"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <Input width={"100%"} id="address" onBlur={() => trigger('address')} htmlFor="address" errorText={validateError('address', errors)} onChange={onChange} placeholder={''} title={t('checkin.address.forms.label.address')} value={value} />
                            )
                            }
                        />

                        <Controller
                            name="complement"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <Input width={"100%"} id="complement" onBlur={() => trigger('complement')} htmlFor="complement" errorText={validateError('complement', errors)} onChange={onChange} placeholder={''} title={t('checkin.address.forms.label.complement')} value={value} />
                            )
                            }
                        />

                        <Controller
                            name="city"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                                <Input width={"100%"} id="city" onBlur={() => trigger('city')} htmlFor="city" errorText={validateError('city', errors)} onChange={onChange} placeholder={''} title={t('checkin.address.forms.label.city')} value={value} />
                            )
                            }
                        />
                    </S.RigthColumn>
                </S.FormContainer>
                <FooterWithButtons handleBackStep={handleBack} handleNextStep={handleSubmit(OnSubmit)} />
            </S.AddressContainer>
        ) : (
            <S.AddressContainer>
                <h1>{t('checkin.address.title')}</h1>
                <h4>{t('checkin.address.subtitle')}</h4>
                <S.FormContainerInternational>
                    <Controller
                        name="fullAddress"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <Input width={"100%"} id="fullAddress" onBlur={() => trigger('fullAddress')} htmlFor="fullAddress" errorText={validateError('fullAddress', errors)} onChange={onChange} placeholder={''} title={t('checkin.address.forms.label.fullAddress')} value={value} />
                        )
                        }
                    />
                    <Controller
                        name="cityInternational"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <Input width={"100%"} id="cityInternational" onBlur={() => trigger('cityInternational')} htmlFor="cityInternational" errorText={validateError('cityInternational', errors)} onChange={onChange} placeholder={''} title={t('checkin.address.forms.label.city')} value={value} />
                        )
                        }
                    />
                    <Controller
                        name="province"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <Input width={"100%"} id="province" onBlur={() => trigger('province')} htmlFor="province" errorText={validateError('province', errors)} onChange={onChange} placeholder={''} title={t('checkin.address.forms.label.province')} value={value} />
                        )
                        }
                    />
                </S.FormContainerInternational>
                <FooterWithButtons handleBackStep={handleBack} handleNextStep={handleSubmit(OnSubmit)} />
            </S.AddressContainer>
        )}


    </>
    )
}